import type { ReactNode } from "react";
import type {
  CvEntry,
  CvModel,
  CvSection,
} from "@/lib/tool-kit/admissions/academic-cv";
import { toRuns } from "@/lib/tool-kit/admissions/academic-cv-export";

/*
 * The CV as it will read, drawn from the same model the downloads use. Every word on
 * it is the writer's own or a section heading passed in by the widget, so nothing here
 * needs a string of its own.
 */

function Inline({ text, boldName }: { text: string; boldName: string }) {
  return (
    <>
      {toRuns(text, boldName).map((run, index) => {
        let node: ReactNode = run.text;
        if (run.italic) {
          node = <em>{node}</em>;
        }
        if (run.bold) {
          node = <strong>{node}</strong>;
        }
        // Runs are positional pieces of one line.
        // biome-ignore lint/suspicious/noArrayIndexKey: positional runs
        return <span key={index}>{node}</span>;
      })}
    </>
  );
}

function Details({ entry, boldName }: { entry: CvEntry; boldName: string }) {
  if (entry.details.length === 0) {
    return null;
  }
  return (
    <ul className="mt-1 list-disc space-y-0.5 pl-5">
      {entry.details.map((detail) => (
        <li key={detail}>
          <Inline boldName={boldName} text={detail} />
        </li>
      ))}
    </ul>
  );
}

function SectionBody({
  section,
  boldName,
}: {
  section: CvSection;
  boldName: string;
}) {
  if (section.kind === "paragraph") {
    return (
      <p className="whitespace-pre-wrap">
        <Inline boldName={boldName} text={section.entries[0]?.text ?? ""} />
      </p>
    );
  }
  const dated = section.entries.some((entry) => entry.date !== "");
  return (
    <ul className="space-y-1.5">
      {section.entries.map((entry, index) => (
        <li
          className={
            dated ? "grid gap-x-4 sm:grid-cols-[8rem_1fr]" : "pl-6 -indent-6"
          }
          // Entries are positional: two lines may be identical.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional entries
          key={index}
        >
          {dated ? <span className="font-semibold">{entry.date}</span> : null}
          <span>
            {entry.number === undefined ? "" : `${entry.number}. `}
            <Inline boldName={boldName} text={entry.text} />
            <Details boldName={boldName} entry={entry} />
          </span>
        </li>
      ))}
    </ul>
  );
}

export function CvPreview({
  model,
  boldName,
}: {
  model: CvModel;
  boldName: string;
}) {
  const { header } = model;
  return (
    <article className="space-y-5 rounded-lg border border-border bg-card p-5 text-foreground text-sm leading-relaxed sm:p-8">
      <header className="space-y-1 text-center">
        <h3 className="font-semibold text-2xl">{header.name}</h3>
        {header.title ? <p>{header.title}</p> : null}
        {header.affiliation ? <p>{header.affiliation}</p> : null}
        {header.contacts.length > 0 ? (
          <p className="text-muted-foreground text-xs">
            {header.contacts.join(" · ")}
          </p>
        ) : null}
      </header>
      {model.sections.map((section) => (
        <section className="space-y-2" key={section.id}>
          <h4 className="border-border border-b pb-1 font-semibold text-xs uppercase tracking-wide">
            {section.heading}
          </h4>
          <SectionBody boldName={boldName} section={section} />
        </section>
      ))}
    </article>
  );
}
